import { HIGHER_TIER_REQUIRED_MESSAGE } from "@/components/Intelligence/constants";
import { getAuthActions, getAuthContext } from "@/store/auth";
import axios, {
  AxiosAdapter,
  AxiosError,
  AxiosProgressEvent,
  AxiosRequestConfig,
  AxiosRequestTransformer,
  AxiosResponse,
  ResponseType
} from "axios";
import axiosRetry from "axios-retry";
import { stringify } from "qs";
import { refreshSession, decodeToken } from "./workos";
import { explorerPrefixedPath, sleep } from ".";
import env from "@/env";
import type { AuthToken } from "@/types/auth";

const API_REQUEST_RETRY_LIMIT = 2;
const SESSION_EXPIRY_DELAY_MS = 200;

export const GUEST_USER_ID_HEADER = "X-Guest-Session-ID";

export const GUEST_USER_MIGRATION_HEADER = "X-Reconciliation-Guest-Session-ID";

const UNAUTHORISED_STATUS_CODES = [401];
const TOKEN_EXPIRY_SKEW_MS = 60 * 1000; // 60 seconds

let token: string | undefined = undefined;
let inFlightRefresh: Promise<void> | null = null;

export const setToken = (newToken: string | undefined) => {
  console.log("setToken New token");
  token = newToken;
};

export const getToken = () => {
  console.log("getToken Token");
  return token;
};

axiosRetry(axios, {
  retries: API_REQUEST_RETRY_LIMIT,
  retryCondition(error) {
    if (error?.code === "ERR_NETWORK") {
      return false;
    }
    switch (error?.response?.status) {
      case 404:
      case 400:
      case 422:
      case 429:
      case 403:
        return false;
      case 415:
        return false;
      default:
        return true;
    }
  },
  shouldResetTimeout: true
});

// Ensure every axios request (including retries triggered by axios-retry) uses
// the latest in-memory access token value. This prevents retries from being
// sent with a stale Token header after a successful refresh.
axios.interceptors.request.use((config) => {
  // Ensure headers object exists and is mutable
  // Axios in the browser will give us a plain object here.
  const headers = (config.headers || {}) as Record<string, unknown>;
  headers.Token = token;
  config.headers = headers as typeof config.headers;
  return config;
});

/** Shown when inventory disappears mid create/deploy (raw API: "Hardware X not known"). */
export const HARDWARE_OUT_OF_STOCK_USER_MESSAGE =
  "This hardware is no longer available or is out of stock. Please select different hardware.";

const HARDWARE_NOT_KNOWN_MESSAGE_REGEX = /^Hardware\s+.+\s+not known\.?$/i;
export const NO_HARDWARE_FOUND_MESSAGE = "No hardware found for provided filters.";

/** Raw backend "Hardware <name> not known" (pre or post friendly mapping). */
export const isHardwareNotKnownMessage = (message: string): boolean => {
  const normalized = message.trim();
  return (
    HARDWARE_NOT_KNOWN_MESSAGE_REGEX.test(normalized) ||
    normalized === HARDWARE_OUT_OF_STOCK_USER_MESSAGE
  );
};

/**
 * True when the API (or a mapped message) indicates selected hardware is
 * unavailable / out of stock during pricing or deploy.
 */
export const isHardwareInventoryUnavailableMessage = (message: string): boolean => {
  const normalized = message.trim();
  if (!normalized) return false;
  if (isHardwareNotKnownMessage(normalized)) return true;
  if (normalized === NO_HARDWARE_FOUND_MESSAGE) return true;
  return false;
};

const extractApiErrorMessage = (data: unknown): string | undefined => {
  if (!data || typeof data !== "object") return undefined;
  const { error, detail } = data as { error?: unknown; detail?: unknown };
  const candidate = error ?? detail;

  if (typeof candidate === "string") {
    return candidate;
  }

  if (Array.isArray(candidate)) {
    const parts = candidate
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object" && "msg" in item) {
          const msg = (item as { msg?: unknown }).msg;
          return typeof msg === "string" ? msg : undefined;
        }
        return undefined;
      })
      .filter((part): part is string => Boolean(part));
    return parts.length > 0 ? parts.join(", ") : undefined;
  }

  return undefined;
};

/** Map legacy backend credits wording to the product term “paid credits”. */
export const rewriteCreditsTerminology = (message: string) => {
  return message.replace(/\bpurchased credits\b/gi, (match) =>
    match[0] === "P" ? "Paid credits" : "paid credits"
  );
};

export const toFriendlyPublicError = (e: unknown) => {
  const { name } = e as Error;

  if (name === "WalletSignTransactionError") {
    return new Error("Cancelled by user");
  }

  if (e instanceof AxiosError) {
    if (e.response?.status === 402) {
      return new Error(HIGHER_TIER_REQUIRED_MESSAGE);
    }

    const errorMessage = extractApiErrorMessage(e.response?.data);

    if (errorMessage) {
      return new Error(toFriendlyErrorMessage(errorMessage));
    }
  }

  if (e instanceof Error && e.message) {
    const friendlyMessage = toFriendlyErrorMessage(e.message);
    if (friendlyMessage !== e.message) {
      return new Error(friendlyMessage);
    }
  }

  return e as Error;
};

export const toFriendlyError = (e: unknown) => {
  if (e instanceof AxiosError) {
    if (e.response?.status === 402) {
      return new Error(HIGHER_TIER_REQUIRED_MESSAGE);
    }

    const errorMessage = extractApiErrorMessage(e.response?.data);

    if (errorMessage) {
      return new Error(toFriendlyErrorMessage(errorMessage));
    }

    const errorMessageFromCode = getErrorMessageFromCode(e.code);
    if (errorMessageFromCode) {
      return new Error(errorMessageFromCode);
    }

    return new Error("We are currently experiencing high load. Please try again later.");
  }

  return new Error("Something went wrong. Please try again or come back later.");
};

const ensureFreshToken = async () => {
  const { token: currentToken, isLoggedIn } = getAuthContext();

  if (!currentToken || !isLoggedIn) {
    return;
  }

  const decoded = decodeToken<AuthToken>(currentToken);

  if (!decoded?.exp) {
    // If we cannot read expiry, fall back to 401-driven refresh behaviour
    return;
  }

  const expiresAtMs = decoded.exp * 1000;
  const now = Date.now();

  // If token is still comfortably valid, skip refresh
  if (expiresAtMs - now > TOKEN_EXPIRY_SKEW_MS) {
    return;
  }

  // If a refresh is already in progress, just wait for it
  if (inFlightRefresh) {
    try {
      await inFlightRefresh;
    } catch {
      // Best-effort: let subsequent request/401 handling decide what to do
    }
    return;
  }

  inFlightRefresh = (async () => {
    try {
      const refreshResult = await refreshSession();
      // This updates both the global token (via setToken) and auth store state
      getAuthActions().setAccessToken(refreshResult.accessToken);
    } finally {
      inFlightRefresh = null;
    }
  })();

  try {
    await inFlightRefresh;
  } catch {
    // Swallow refresh errors here; the upcoming request can still fail with 401
    // and will be handled by the existing interceptor logic.
  }
};

export const executeAPIRequest = async <T>({
  method,
  url,
  options
}: {
  method: "get" | "post" | "delete" | "put" | "patch";
  url: string;
  options?: {
    data?: unknown;
    errorPrefix?: string;
    abortController?: AbortController;
    onUploadProgress?: ((progressEvent: AxiosProgressEvent) => void) | undefined;
    timeout?: number;
    headers?: Record<string, unknown>;
    publicError?: boolean;
    axiosRetries?: number;
    replaceUrl?: boolean;
    responseType?: ResponseType;
    adapter?: AxiosAdapter;
    fullResponse?: boolean;
    transformRequest?: AxiosRequestTransformer | AxiosRequestTransformer[];
  };
}) => {
  const {
    transformRequest,
    fullResponse,
    abortController,
    timeout,
    publicError,
    axiosRetries,
    replaceUrl,
    responseType,
    adapter,
    onUploadProgress
  } = options || {};

  // Ensure we have a fresh access token (or at least attempt to) before
  // sending the request so the user doesn't get hit with avoidable 401s.
  await ensureFreshToken();

  // Construct full URL: use url as-is if replaceUrl is true, otherwise prepend API_URL
  const fullUrl = replaceUrl ? url : `${env.API_URL}${url}`;

  const headers = {
    ...options?.headers,
    Token: token,
    "Frontend-Version": env.APP_RELEASE_VERSION
  };

  const config: AxiosRequestConfig = {
    headers,
    responseType,
    adapter,
    onUploadProgress,
    signal: abortController?.signal,
    timeout: typeof timeout === "number" ? timeout : env.API_DEFAULT_TIMEOUT,
    ...(transformRequest ? { transformRequest } : {}),
    ...(typeof axiosRetries === "number"
      ? {
          "axios-retry": {
            retries: axiosRetries
          }
        }
      : {})
  };

  try {
    let response: AxiosResponse<T>;

    switch (method) {
      case "delete":
        response = await axios.delete(fullUrl, config);
        break;

      case "put":
      case "post":
      case "patch":
        response = await axios[method](fullUrl, options?.data, config);
        break;
      default:
        response = await axios.get(fullUrl, config);
        break;
    }

    if (fullResponse) {
      return response as T;
    }

    return response.data as T;
  } catch (e) {
    if (publicError) {
      throw e;
    }
    throw toFriendlyError(e);
  }
};
const ERROR_CODE_MAP = {
  ERR_NAME_NOT_RESOLVED: "Unable to resolve domain",
  [AxiosError.ERR_NETWORK]: "Network problem",
  [AxiosError.ERR_FR_TOO_MANY_REDIRECTS]: "ERR_FR_TOO_MANY_REDIRECTS error",
  [AxiosError.ERR_BAD_OPTION_VALUE]: "ERR_BAD_OPTION_VALUE error",
  [AxiosError.ERR_BAD_OPTION]: "ERR_BAD_OPTION error",
  [AxiosError.ERR_DEPRECATED]: "ERR_DEPRECATED error",
  [AxiosError.ERR_BAD_RESPONSE]: "ERR_BAD_RESPONSE error",
  [AxiosError.ERR_BAD_REQUEST]: "ERR_BAD_REQUEST error",
  [AxiosError.ECONNABORTED]: "ECONNABORTED error",
  [AxiosError.ETIMEDOUT]: "Request timed out",
  [AxiosError.ERR_CANCELED]: "Request cancelled"
};

const getErrorMessageFromCode = (code: string | undefined): string | undefined => {
  if (!code) return undefined;
  const upperCode = code.toUpperCase();
  return ERROR_CODE_MAP[upperCode as keyof typeof ERROR_CODE_MAP];
};

const errorMessageMap = {
  "Error getting user email from external provider":
    "Please use an account that has an email associated to it."
};

export const toFriendlyErrorMessage = (message: string) => {
  if (message in errorMessageMap) {
    return errorMessageMap[message as keyof typeof errorMessageMap];
  }

  // Backend uses "Hardware <name> not known" when a GPU that was available at
  // selection time disappears before price/deploy completes.
  if (HARDWARE_NOT_KNOWN_MESSAGE_REGEX.test(message.trim())) {
    return HARDWARE_OUT_OF_STOCK_USER_MESSAGE;
  }

  return rewriteCreditsTerminology(message);
};

let isLoggingOut = false;

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.code && ["ERR_CANCELED"].includes(error.code)) {
      return Promise.reject(error);
    }

    const context = getAuthContext();
    const { status } = error.response || {};

    // Status code is 401 try to refresh token
    if (
      context.isLoggedIn &&
      UNAUTHORISED_STATUS_CODES.includes(status) &&
      !isLoggingOut &&
      env.LOGOUT_ON_API_ERROR
    ) {
      try {
        console.log("logoutIfSessionExpired Trying to logout");
        await logoutIfSessionExpired();
      } catch (e) {
        console.error(e);
      }
    }

    return Promise.reject(error);
  }
);

const logoutIfSessionExpired = async () => {
  if (isLoggingOut) {
    return;
  }
  const result = await shouldLogoutIfSessionExpired();
  console.log("logoutIfSessionExpired Result", result);
  if (!result) {
    return;
  }

  isLoggingOut = true;

  await sleep(SESSION_EXPIRY_DELAY_MS);

  await getAuthActions().logout(
    `${explorerPrefixedPath("/login")}?${stringify({
      errorTitle: "io.net",
      errorMessage: "Your session has expired. Please sign back in."
    })}`
  );
};

const shouldLogoutIfSessionExpired = async () => {
  try {
    const { token, isLoggedIn } = getAuthContext();
    if (!token || !isLoggedIn) {
      return false;
    }

    const refreshResult = await refreshSession();
    // setAccessToken will propagate the new token everywhere (including this module)
    getAuthActions().setAccessToken(refreshResult.accessToken);
  } catch (e) {
    console.error(e);
    // Tried to refresh session and failed
    return true;
  }

  // Was able to refresh token
  return false;
};

export const errorIndicatesCancelled = (e: unknown) => {
  return ["request cancelled"].includes((e as Error).message.toLowerCase());
};
